// src/services/PdfExportService.js - Enhanced with clickable index links
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import EvidenceDataService from './EvidenceDataService';
import StampImageService from './StampImageService';
import { IndexService } from '../components/index/IndexGenerator';
import { STAMP_DIMENSIONS } from '../components/stamp/Stamp';

/**
 * Format page number with 6-digit zero padding (e.g., 1 -> "000001")
 * This matches the bates number format used in the index
 * @param {number} pageNum - The page number to format
 * @returns {string} - Formatted 6-digit page number
 */
const formatPageNumber = (pageNum) => {
  return pageNum.toString().padStart(6, '0');
};

class PdfExportService {
  /**
   * Create stamp on page - converts percentages to PDF points for export
   */
  static async createStampOnPage(page, stampData, stampConfig, documentIndex, caseData) {
    console.log('🎯 Creating stamp using page units for document:', documentIndex);
    
    try {
      // Get page dimensions
      const pageSize = page.getSize();
      const pageWidth = pageSize.width;
      const pageHeight = pageSize.height;
      
      console.log('📄 PDF page size:', { pageWidth, pageHeight });
      console.log('📍 Stamp data:', stampData);
      
      let x, y;
      
      // stampLocation is stored as PDF POINTS in database - use directly!
      if (stampData?.stampLocation && stampData?.pdfDimensions) {
        console.log('✅ Using PDF points directly from database');
        console.log('📍 Position in PDF points:', stampData.stampLocation);
        
        // Just use the values directly - they're already in PDF coordinate system
        x = stampData.stampLocation.x;
        y = stampData.stampLocation.y;
        
        // Clamp to ensure stamp stays on page
        x = Math.max(0, Math.min(x, pageWidth - STAMP_DIMENSIONS.BASE_WIDTH));
        y = Math.max(0, Math.min(y, pageHeight - STAMP_DIMENSIONS.BASE_HEIGHT));
        
      } 
      // FALLBACK: Default position if no stampLocation
      else {
        console.log('⚠️ No stampLocation found - using default bottom-right position');
        const marginPoints = 18; // 0.25 inches
        x = pageWidth - STAMP_DIMENSIONS.BASE_WIDTH - marginPoints;
        y = marginPoints;
      }
      
      // Use actual stamp dimensions
      const width = STAMP_DIMENSIONS.BASE_WIDTH;
      const height = STAMP_DIMENSIONS.BASE_HEIGHT;
      
      console.log('📍 Final stamp position and size:', { 
        x: x.toFixed(1), 
        y: y.toFixed(1), 
        width,
        height,
        source: stampData?.stampLocation ? 'database-pdf-points' : 'default'
      });
      
      // Generate stamp image
      console.log('📸 Generating stamp image...');
      const stampImageData = await StampImageService.generateStampBlob(
        stampConfig,
        documentIndex,
        caseData,
        2 // Standard scale for export
      );
      
      if (!stampImageData || stampImageData.length === 0) {
        throw new Error('Generated stamp image data is empty');
      }
      
      console.log('✅ Stamp image generated, size:', stampImageData.length, 'bytes');
      
      // Embed and draw the image
      const pdfDoc = page.doc;
      const embeddedImage = await pdfDoc.embedPng(stampImageData);
      
      page.drawImage(embeddedImage, {
        x: x,
        y: y, 
        width: width,
        height: height,
      });
      
      console.log('✅ Stamp successfully added to PDF page');
      
    } catch (error) {
      console.error('❌ Error creating stamp on page:', error);
      throw error; // Re-throw to ensure exports fail loudly when stamps fail
    }
  }

  /**
   * Create stamp on page using pre-generated stamp image
   * OPTIMIZED: No stamp generation needed, just embed the pre-made image
   */
  static async createStampOnPageWithPregenerated(page, stampData, preGeneratedStamp) {
    try {
      const pageSize = page.getSize();
      const pageWidth = pageSize.width;
      const pageHeight = pageSize.height;
      
      let x, y;
      
      // Use stamp location from database
      if (stampData?.stampLocation && stampData?.pdfDimensions) {
        x = stampData.stampLocation.x;
        y = stampData.stampLocation.y;
        
        // Clamp to ensure stamp stays on page
        x = Math.max(0, Math.min(x, pageWidth - STAMP_DIMENSIONS.BASE_WIDTH));
        y = Math.max(0, Math.min(y, pageHeight - STAMP_DIMENSIONS.BASE_HEIGHT));
      } else {
        // FALLBACK: Default position
        const marginPoints = 18;
        x = pageWidth - STAMP_DIMENSIONS.BASE_WIDTH - marginPoints;
        y = marginPoints;
      }
      
      const width = STAMP_DIMENSIONS.BASE_WIDTH;
      const height = STAMP_DIMENSIONS.BASE_HEIGHT;
      
      // Use pre-generated stamp (no generation overhead!)
      const pdfDoc = page.doc;
      const embeddedImage = await pdfDoc.embedPng(preGeneratedStamp);
      
      page.drawImage(embeddedImage, {
        x: x,
        y: y, 
        width: width,
        height: height,
      });
      
    } catch (error) {
      console.error('❌ Error creating stamp on page:', error);
      throw error;
    }
  }

  /**
   * Add page number to bottom center of page
   * @param {Object} page - pdf-lib Page object
   * @param {number} pageNumber - The absolute page number in the merged document (1-based)
   */
  static async addPageNumberToPage(page, pageNumber) {
    try {
      const pageSize = page.getSize();
      const pageWidth = pageSize.width;
      const pageHeight = pageSize.height;
      
      // Format page number as 6 digits
      const formattedPageNumber = formatPageNumber(pageNumber);
      
      // Embed font for page number
      const pdfDoc = page.doc;
      const font = await pdfDoc.embedFont(StandardFonts.TimesRoman);
      const fontSize = 10;
      
      // Calculate text dimensions
      const textWidth = font.widthOfTextAtSize(formattedPageNumber, fontSize);
      
      // Position at bottom center
      const marginBottom = 36; // 0.5 inches from bottom
      const x = (pageWidth - textWidth) / 2; // Center horizontally
      const y = marginBottom;
      
      // Draw page number
      page.drawText(formattedPageNumber, {
        x: x,
        y: y,
        size: fontSize,
        font: font,
        color: rgb(0, 0, 0), // Black text
      });
      
    } catch (error) {
      console.error('❌ Error adding page number to page:', error);
      throw error; // Fail loudly as requested
    }
  }

  /**
   * Export a single PDF with stamp
   * @param {Object} evidence - Single evidence document to export
   * @param {Object} stampConfig - Stamp configuration
   * @param {Object} caseData - Case information
   * @returns {Promise<void>} - Downloads the PDF
   */
  static async exportSinglePdf(evidence, stampConfig, caseData) {
    console.log('📄 Starting single PDF export for:', evidence.displayName || evidence.name);
    
    try {
      // Clear cache at start
      StampImageService.clearCache();
      
      // Validate stamp configuration
      const validatedStampConfig = EvidenceDataService.validateStampConfig(stampConfig);
      console.log('✅ Stamp configuration validated');

      // Use searchable PDF for export (required for text search)
      const pdfUrl = evidence.searchablePdfUrl;

      if (!pdfUrl) {
        throw new Error('No searchable PDF URL found. File must be fully processed first.');
      }
      
      // Validate that evidence has required stamp data
      if (!evidence.pdfDimensions) {
        throw new Error(`Missing PDF dimensions. File must be processed by server first.`);
      }
      
      if (!evidence.stampLocation) {
        console.warn(`⚠️ No stamp location found, will use default position`);
      }
      
      // Load source PDF
      const response = await fetch(pdfUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status}`);
      }
      
      const pdfBuffer = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBuffer);
      
      // Get first page and add stamp
      const firstPage = pdfDoc.getPage(0);
      await this.createStampOnPage(
        firstPage, 
        evidence,
        validatedStampConfig, 
        evidence.documentIndex || 0,
        caseData
      );
      
      // Generate final PDF
      console.log('📄 Generating final PDF...');
      const pdfBytes = await pdfDoc.save();
      
      // Generate filename
      const documentName = evidence.displayName || evidence.name || 'Document';
      const cleanName = documentName.replace('.pdf', '').replace(/[^a-z0-9]/gi, '_');
      const filename = `${cleanName}.pdf`;
      
      // Create blob and trigger download
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log(`✅ Single PDF downloaded as: ${filename}`);
      
    } catch (error) {
      console.error('❌ Single PDF export failed:', error);
      throw error;
    } finally {
      // Clean up cache
      StampImageService.clearCache();
    }
  }

  /**
   * Export multiple PDFs with stamps as a single merged PDF
   * Enhanced to work with both page units and legacy percentages
   */
  static async exportCombinedPdf(evidenceList, stampConfig, caseData, statusCallback = () => {}, documentSetInfo = null) {
    console.log('📦 Starting PDF export with page units for', evidenceList.length, 'documents');
    
    try {
      // Clear cache at start
      StampImageService.clearCache();
      
      // Validate stamp configuration
      const validatedStampConfig = EvidenceDataService.validateStampConfig(stampConfig);
      console.log('✅ Stamp configuration validated');
      
      // Create new PDF document for merged output
      const mergedPdf = await PDFDocument.create();
      
      // Generate index and add (WITHOUT links yet)
      statusCallback('Generating index page...');
      await this.addIndexPage(mergedPdf, evidenceList, validatedStampConfig, caseData, documentSetInfo);
      
      // OPTIMIZATION: Pre-generate all stamps in parallel
      statusCallback('Pre-generating stamps...');
      console.log('🎨 Pre-generating all stamps in parallel...');
      const startStampGen = Date.now();
      
      const stampPromises = evidenceList.map((evidence, index) => 
        StampImageService.generateStampBlob(
          validatedStampConfig,
          evidence.documentIndex !== undefined ? evidence.documentIndex : index,
          caseData,
          2
        )
      );
      
      const preGeneratedStamps = await Promise.all(stampPromises);
      
      const stampGenTime = Date.now() - startStampGen;
      console.log(`✅ All ${evidenceList.length} stamps generated in ${stampGenTime}ms`);
      
      // Determine optimal batch size based on file sizes
      const avgFileSize = evidenceList.reduce((sum, e) => sum + (e.fileSize || 0), 0) / evidenceList.length;
      const batchSize = avgFileSize > 5 * 1024 * 1024 ? 3 : (avgFileSize > 1 * 1024 * 1024 ? 5 : 8);
      console.log(`📦 Processing PDFs in batches of ${batchSize}`);
      
      let processedCount = 0;
      
      // Process documents in batches
      for (let batchStart = 0; batchStart < evidenceList.length; batchStart += batchSize) {
        const batch = evidenceList.slice(batchStart, batchStart + batchSize);
        const batchNum = Math.floor(batchStart / batchSize) + 1;
        const totalBatches = Math.ceil(evidenceList.length / batchSize);
        
        // STEP 1: Load all PDFs in batch in parallel (fast!)
        const loadedPdfs = await Promise.all(batch.map(async (evidence, batchIndex) => {
          const globalIndex = batchStart + batchIndex;
          
          try {

            // Use searchable PDF for export
            const pdfUrl = evidence.searchablePdfUrl;

            if (!pdfUrl) {
              console.warn(`⚠️ No searchable PDF URL found for ${evidence.id}, skipping`);
              return null;
            }
            
            // Validate that evidence has required stamp data
            if (!evidence.pdfDimensions) {
              throw new Error(`Missing PDF dimensions for ${evidence.id}`);
            }
            
            if (!evidence.stampLocation) {
              console.warn(`⚠️ No stamp location found for ${evidence.id}, will use default position`);
            }
            
            // Fetch and load PDF
            const response = await fetch(pdfUrl);
            if (!response.ok) {
              throw new Error(`Failed to fetch PDF: ${response.status}`);
            }
            
            const pdfBuffer = await response.arrayBuffer();
            const sourcePdf = await PDFDocument.load(pdfBuffer);
            
            return {
              evidence,
              sourcePdf,
              globalIndex
            };
            
          } catch (error) {
            console.error(`❌ Error loading ${evidence.displayName || evidence.id}:`, error);
            throw new Error(`Failed to load ${evidence.displayName || evidence.id}: ${error.message}`);
          }
        }));
        
        // STEP 2: Add loaded PDFs to merged document in order (preserves document order!)
        for (const loadedPdf of loadedPdfs) {
          if (!loadedPdf) continue; // Skip if null (no URL)
          
          const { evidence, sourcePdf, globalIndex } = loadedPdf;
          
          try {
            
            // Copy all pages
            const pageIndices = sourcePdf.getPageIndices();
            const copiedPages = await mergedPdf.copyPages(sourcePdf, pageIndices);
            
            // Add pages with stamps and page numbers
            for (let pageIndex = 0; pageIndex < copiedPages.length; pageIndex++) {
              const copiedPage = copiedPages[pageIndex];
              const addedPage = mergedPdf.addPage(copiedPage);
              
              // Add stamp to first page only (using pre-generated stamp)
              if (pageIndex === 0) {
                await this.createStampOnPageWithPregenerated(
                  addedPage,
                  evidence,
                  preGeneratedStamps[globalIndex]
                );
              }
              
              // Add page number to ALL pages
              const absolutePageNumber = evidence.startPage + pageIndex;
              await this.addPageNumberToPage(addedPage, absolutePageNumber);
            }
            
            processedCount++;
          
          } catch (error) {
            console.error(`❌ Error processing ${evidence.displayName || evidence.id}:`, error);
            
            // Export should fail loudly when stamp processing fails
            throw new Error(`Failed to process ${evidence.displayName || evidence.id}: ${error.message}`);
          }
        };
      }
      
      if (processedCount === 0) {
        throw new Error('No documents were successfully processed');
      }
      
      // NOW add clickable links to the index page (after all documents are merged)
      await this.addIndexPageLinks(mergedPdf, evidenceList, validatedStampConfig);
      
      // Generate final PDF
      const pdfBytes = await mergedPdf.save();
      
      console.log(`✅ Export completed successfully: ${pdfBytes.length} bytes`);
      
      // DOWNLOAD THE PDF
      console.log('📥 Triggering download...');
      
      // Generate filename with case name and timestamp
      const caseName = caseData?.name || 'Case';
      const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const filename = `${caseName}_Exhibits_${timestamp}.pdf`;
      
      // Create blob and trigger download
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log(`✅ PDF downloaded as: ${filename}`);
      
      return pdfBytes;
      
    } catch (error) {
      console.error('❌ Export failed:', error);
      statusCallback(`Export failed: ${error.message}`);
      throw error;
    } finally {
      // Clean up cache
      StampImageService.clearCache();
    }
  }

  /**
   * Add index page to merged PDF
   * NOTE: Links are added AFTER all documents are merged, not here
   */
  static async addIndexPage(mergedPdf, evidenceList, stampConfig, caseData, documentSetInfo = null ) {
    console.log('📋 Generating index page...');
    
    try {
      // Generate the index PDF using the actual IndexService (with @react-pdf/renderer)
      const indexBlob = await IndexService.generateIndexPDF(evidenceList, stampConfig, caseData, documentSetInfo);
      
      if (!indexBlob) {
        throw new Error('IndexService.generateIndexPDF returned null or undefined');
      }
      
      
      // Convert Blob to ArrayBuffer
      const indexArrayBuffer = await indexBlob.arrayBuffer();
      
      // Load the index PDF with pdf-lib
      const indexPdf = await PDFDocument.load(indexArrayBuffer);
      

      // Copy ALL pages from the index PDF
      const indexPageCount = indexPdf.getPageCount();
      const indexPageIndices = Array.from({ length: indexPageCount }, (_, i) => i);
      const indexPages = await mergedPdf.copyPages(indexPdf, indexPageIndices);

      // Insert all index pages at the beginning (index 0)
      for (let i = 0; i < indexPages.length; i++) {
        mergedPdf.insertPage(i, indexPages[i]);
      }
      
    } catch (error) {
      console.error('❌ Error generating index page:', error);
      throw error;
    }
  }



  /**
   * Add clickable internal links to the index page with DYNAMIC row heights
   * Uses actual font measurements to determine text wrapping
   * 
   * DEBUG MODE: Set SHOW_DEBUG_BOXES = true to show red rectangles around clickable areas
   */
  static async addIndexPageLinks(mergedPdf, evidenceList, stampConfig) {
    
    // DEBUG SETTING: Set to true to show red boxes around clickable areas
    const SHOW_DEBUG_BOXES = false;
    
    try {
      const indexPage = mergedPdf.getPage(0);
      const { width: pageWidth, height: pageHeight } = indexPage.getSize();
      
      // CRITICAL: Embed Times-Roman font for precise text measurement
      const font = await mergedPdf.embedFont(StandardFonts.TimesRoman);
      const fontSize = 12;
      
      // Clear any existing annotations
      const context = indexPage.doc.context;
      indexPage.node.set(context.obj('Annots'), context.obj([]));
      
      // Test: Draw a large red rectangle at the top of the page to verify drawing works
      if (SHOW_DEBUG_BOXES) {
        console.log('🔴 DEBUG MODE: Red boxes enabled');
        try {
          indexPage.drawRectangle({
            x: 50,
            y: pageHeight - 150,
            width: 200,
            height: 100,
            color: rgb(1, 0, 0),
            opacity: 0.5,
          });
          console.log('✅ Test rectangle drawn successfully');
        } catch (err) {
          console.error('❌ Failed to draw test rectangle:', err);
        }
      }
      
      // ==========================================
      // LINK POSITIONING CONSTANTS  
      // ==========================================
      
      // Page layout
      const MARGIN = 72; // 1 inch
      
      // Title area (3 lines + margins)
      const TITLE_HEIGHT = 72;
      
      // Table dimensions
      const HEADER_HEIGHT = 34;
      const BASE_ROW_HEIGHT = 30.4; // Height for single-line row
      const LINE_HEIGHT = 13.5; // Additional height per wrapped line (12pt font + 2pt spacing)
      
      // Clickable area sizing and positioning
      const CLICKABLE_HEIGHT = 20;
      const TOP_OFFSET = 4; // Fixed offset from top of row (adjust this to align with text)
      
      // Column widths (must match IndexGenerator.js)
      const EXHIBIT_WIDTH = 0.07;
      const DOCUMENT_WIDTH = 0.49;
      
      // Calculate available widths
      const tableWidth = pageWidth - (2 * MARGIN);
      const documentColumnWidth = tableWidth * DOCUMENT_WIDTH;
      
      // Calculate usable width for text (accounting for padding)
      const COLUMN_PADDING = 18; // 8pt padding on each side
      const usableWidth = documentColumnWidth - COLUMN_PADDING;
      
      // console.log('📏 Font measurement setup:', {
      //  font: 'Times-Roman',
      //  fontSize,
      //  documentColumnWidth: documentColumnWidth.toFixed(1),
      //  usableWidth: usableWidth.toFixed(1)
      // });
      
      // Calculate starting positions
      const tableTop = pageHeight - MARGIN - TITLE_HEIGHT;
      const firstDataRowTop = tableTop - HEADER_HEIGHT;
      
      // Exhibit column dimensions
      const exhibitColumnStart = MARGIN;
      const exhibitColumnWidth = tableWidth * EXHIBIT_WIDTH;
      
      // Get filtered display data
      const displayData = evidenceList.filter(doc => {
        const isIndexDoc = doc.isIndex || doc.id === 'INDEX_DOCUMENT';
        return !isIndexDoc;
      });

      console.log('🔗 Adding index links for documents:', displayData.map(d => ({
        id: d.id,
        exhibitId: d.exhibitId,
        startPage: d.startPage
      })));
      
      // Track cumulative vertical position
      let currentTop = firstDataRowTop;
      
      // Add a link for each document row
      displayData.forEach((doc, index) => {
        const exhibitId = doc.exhibitId;
        const targetPage = doc.startPage;
        
        // Get document name and measure its ACTUAL width using the embedded font
        const docName = (doc.name || 'Untitled Document').replace('.pdf', '');
        const textWidth = font.widthOfTextAtSize(docName, fontSize);
        
        // Calculate how many lines this text will wrap to (precise calculation)
        const estimatedLines = Math.max(1, Math.ceil(textWidth / usableWidth));
        
        // Calculate row height based on measured lines
        const rowHeight = BASE_ROW_HEIGHT + ((estimatedLines - 1) * LINE_HEIGHT);
        
        // Position clickable area with fixed TOP_OFFSET from top of row
        // All extra space from wrapped text goes to the bottom
        const rowBottom = currentTop - TOP_OFFSET - CLICKABLE_HEIGHT;
        
        // Convert to 0-based page index
        const targetPageIndex = targetPage;
        
        /* console.log(`🔗 Link ${index + 1}: "${exhibitId}" - "${docName.substring(0, 30)}${docName.length > 30 ? '...' : ''}"`, {
          nameLength: docName.length,
          textWidth: textWidth.toFixed(1) + 'pt',
          usableWidth: usableWidth.toFixed(1) + 'pt',
          wraps: textWidth > usableWidth,
          estimatedLines,
          rowHeight: rowHeight.toFixed(1),
          rowTop: currentTop.toFixed(1),
          topOffset: TOP_OFFSET,
          clickableTop: (rowBottom + CLICKABLE_HEIGHT).toFixed(1),
          clickableBottom: rowBottom.toFixed(1),
          bottomPadding: (rowHeight - TOP_OFFSET - CLICKABLE_HEIGHT).toFixed(1),
          targetPage,
          targetPageIndex
        }); */
        
        // Verify target page exists
        if (targetPageIndex >= mergedPdf.getPageCount()) {
          console.error(`❌ Target page ${targetPageIndex} doesn't exist! PDF has ${mergedPdf.getPageCount()} pages`);
          return;
        }
        
        // DRAW DEBUG BOX if enabled
        if (SHOW_DEBUG_BOXES) {
          // Draw a filled semi-transparent red rectangle
          indexPage.drawRectangle({
            x: exhibitColumnStart,
            y: rowBottom,
            width: exhibitColumnWidth,
            height: CLICKABLE_HEIGHT,
            color: rgb(1, 0, 0), // Red fill
            opacity: 0.2, // Semi-transparent
          });
          
          // Draw a red border on top
          indexPage.drawRectangle({
            x: exhibitColumnStart,
            y: rowBottom,
            width: exhibitColumnWidth,
            height: CLICKABLE_HEIGHT,
            borderColor: rgb(1, 0, 0), // Red border
            borderWidth: 2,
          });
        }
        
        // Create link annotation
        const targetPageRef = mergedPdf.getPage(targetPageIndex);
        
        const linkAnnotation = context.obj({
          Type: 'Annot',
          Subtype: 'Link',
          Rect: [
            exhibitColumnStart,
            rowBottom,
            exhibitColumnStart + exhibitColumnWidth,
            rowBottom + CLICKABLE_HEIGHT
          ],
          Border: [0, 0, 0], // No border on the link itself
          C: [0, 0, 1], // Blue color
          Dest: [targetPageRef.ref, 'XYZ', null, null, 0],
        });
        
        // Add annotation to page
        const annotsRef = indexPage.node.lookup(context.obj('Annots'));
        
        if (annotsRef) {
          annotsRef.push(linkAnnotation);
        } else {
          const annotsArray = context.obj([linkAnnotation]);
          indexPage.node.set(context.obj('Annots'), annotsArray);
        }
        
        // Move to next row
        currentTop -= rowHeight;
      });
      
      if (SHOW_DEBUG_BOXES) {
        console.log('✅ Successfully added clickable links with DEBUG RED BOXES');
      } else {
        console.log('✅ Successfully added clickable links with font measurement');
      }
      
    } catch (error) {
      console.error('❌ Error adding index page links:', error);
      console.warn('⚠️ Continuing without clickable links');
    }
  }
}

export default PdfExportService;

// Named exports for EvidenceManager
export const exportCombinedPdf = (evidenceList, stampConfig, caseData, statusCallback, documentSetInfo = null) => {
  return PdfExportService.exportCombinedPdf(evidenceList, stampConfig, caseData, statusCallback, documentSetInfo);
};

export const exportSinglePdf = (evidence, stampConfig, caseData) => {
  return PdfExportService.exportSinglePdf(evidence, stampConfig, caseData);
};